import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const PublicThread = () => {
  const { referenceID } = useParams();
  const [thread, setThread] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchThread();
  }, [referenceID]);

  const fetchThread = async () => {
    try {
      const res = await axios.get(`/api/tickets/reference/${referenceID}/`);
      setThread(res.data);
    } catch (err) {
      setMessage('Failed to load thread.');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || isSubmittingReply) return;

    setIsSubmittingReply(true);
    try {
      const fileData = file ? await readFileAsBase64(file) : null;

      await axios.post('/api/tickets/create/', {
        name: thread[0]?.data?.name || '',
        family: thread[0]?.data?.family || '',
        email: thread[0]?.data?.email || '',
        subject: `Re: ${thread[0]?.data?.subject}`,
        description: replyText,
        parent_referenceID: referenceID,
        status: 'open',
        source: thread[0]?.data?.source || 'spice',
        file: fileData,
      });

      setReplyText('');
      setFile(null);
      setMessage('Your reply has been submitted!');
      fetchThread();
    } catch (err) {
      setMessage('Failed to send reply. Please try again.');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  if (loading) return <p>Loading thread...</p>;

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          padding: '10px 20px',
          backgroundColor: '#3498db',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          marginBottom: '20px',
          cursor: 'pointer',
        }}
      >
        ← Back to Inbox
      </button>

      <h1>💬 Thread: {referenceID}</h1>
      <p><strong>Subject:</strong> {thread[0]?.data?.subject}</p>
      <p><strong>Source:</strong> {thread[0]?.data?.source.toUpperCase()}</p>

      <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden', border: '1px solid #eee', padding: '15px', borderRadius: '8px', marginBottom: '30px' }}>
        {[...thread].reverse().map((ticket) => (
          <div
            key={ticket.id}
            style={{
              margin: '15px 0',
              padding: '15px',
              backgroundColor: ticket.assigned_to ? '#e8f4fc' : '#f9f9f9',
              borderRadius: '8px',
              borderLeft: ticket.assigned_to ? '4px solid #3498db' : '4px solid #ccc',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <strong style={{ color: ticket.assigned_to ? '#3498db' : '#555' }}>
                {ticket.assigned_to ? 'Support Team' : 'User'} replied
              </strong>
              <small style={{ color: '#7f8c8d' }}>
                {new Date(ticket.created_at).toLocaleString()}
              </small>
            </div>
            <p style={{ margin: '8px 0', lineHeight: '1.6', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {ticket.data.description}
            </p>
            {ticket.data.file && (
              <a href={`application/octet-stream;base64,${ticket.data.file}`} download="attachment" style={{ color: '#3498db', textDecoration: 'underline' }}>
                📎 Download File
              </a>
            )}
          </div>
        ))}
      </div>

      <h3>📝 Reply to This Thread</h3>
      <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
        Your name, email, and family will be auto-filled from the original ticket. You may edit them.
      </p>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name:</label>
        <input
          type="text"
          defaultValue={thread[0]?.data?.name || ''}
          onChange={(e) => setReplyText(e.target.value)}
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Family:</label>
        <input
          type="text"
          defaultValue={thread[0]?.data?.family || ''}
          onChange={(e) => setReplyText(e.target.value)}
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email:</label>
        <input
          type="email"
          defaultValue={thread[0]?.data?.email || ''}
          onChange={(e) => setReplyText(e.target.value)}
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Message:</label>
        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Type your reply..."
          rows="5"
          wrap="soft"
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '10px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', overflowX: 'hidden' }}
        ></textarea>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Attach File (Optional):</label>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          accept="image/*,.pdf,.doc,.docx,.txt"
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
      </div>

      <button
        onClick={handleReply}
        disabled={!replyText.trim() || isSubmittingReply}
        style={{
          padding: '12px 24px',
          backgroundColor: (!replyText.trim() || isSubmittingReply) ? '#95a5a6' : '#3498db',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: (!replyText.trim() || isSubmittingReply) ? 'not-allowed' : 'pointer',
        }}
      >
        {isSubmittingReply ? 'Sending...' : 'Send Reply'}
      </button>

      {message && <p style={{ color: 'green', marginTop: '15px' }}>{message}</p>}
    </div>
  );
};

export default PublicThread;
